import UseQuery from "@/shared/hooks/use-query.hook";
import { C_PATH_ROUTES } from "@/src-refactor/modules/shared/model/const/path.const";
import {
  useCreateDraftMutation,
  useDraftUpdateMutation,
  useLazyDraftDetailQuery
} from "@/src-refactor/modules/store/qrtk/services/property-draft/property-draft.qrtk";
import {
  CPropertyForm,
  CPropertyFormKeys,
  IPropertyForm
} from "@rf/property/module/components/property-form/util/schemas/property-schema/propertySchema";
import { HToastCatchServerError } from "@rf-helper/cath-error-server";
import { debounce, orderBy, maxBy } from "lodash";
import { useEffect, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import {
  C_REALESTATE_TYPES,
  O_REALESTATE_TYPES,
  O_REALESTATE_TYPES_PROJECT
} from "@/src-refactor/modules/shared/model/const/property/real-estate-type.const";
import { useAutoDraft } from "@rf-hooks/useAutoDraft";
import { feedBackFormProperty } from "../util/const/property-url-regexst";
import useAlert from "@rf-hooks/useAlert";
import { IDraftDetailResponse } from "@/src-refactor/modules/store/qrtk/services/property-draft/responses/draft-detail.response";
import { HGenerateFileData, IFileData } from "@rf-l1/file/helpers/create-file-data";
import { useLazyDraftListQuery } from "@/src-refactor/modules/store/qrtk/services/property-draft/property-draft.qrtk";
import {
  IDraftListResponse,
  IDraftItem
} from "@/src-refactor/modules/store/qrtk/services/property-draft/responses/draft-list.response";
import { HDecodeObj } from "@rf-helper/convert-encode-decode";
import {
  onSendEventBlockerEnd,
  onSendEventBlockerStart
} from "./useUploadingBlocker";
import { C_BUSINESS_TYPE } from "@/src-refactor/modules/shared/model/const/property/business-type.const";

// Patrones regex más robustos para validación de rutas

enum EStatus {
  /**
   * @description is creating a new draft
   */
  CREATING = "creating",
  /**
   * @description is applying a draft
   */
  APPLYING = "applying",
  /**
   * @description is initializing the draft
   */
  INITIALIZING = "initializing",
  /**
   * @description is can update the draft
   */
  CAN_UPDATE = "canUpdate"
}
type Status =
  | EStatus.CREATING
  | EStatus.APPLYING
  | EStatus.INITIALIZING
  | EStatus.CAN_UPDATE;

const usePropertyDraftV2 = () => {
  const FORM_CONFIG = useFormContext();
  const location = useLocation();
  const { getQuery } = UseQuery();
  const params = useParams();
  const navigate = useNavigate();
  const [CreateDraft] = useCreateDraftMutation();
  const [GetDraft] = useLazyDraftDetailQuery();
  const [GetDraftList] = useLazyDraftListQuery();
  const [UpdateDraft] = useDraftUpdateMutation();
  const { isProject, isDraft } = feedBackFormProperty(location.pathname);
  const { warningAlert, infoAlert } = useAlert();
  const status = useRef<Status>(EStatus.INITIALIZING);
  // Configuración de auto-guardado de borrador
  const {} = useAutoDraft(FORM_CONFIG, {
    // crear auto-borrador solo cuando NO estamos en ruta de edición de borrador
    isCreating: isDraft,
    onSave: (data: any) => {
      return new Promise((resolve, reject) => {
        if (status.current === EStatus.CAN_UPDATE) {
          UpdateDraft({
            params: {
              id: Number(params.idDraft)
            },
            body: {
              is_project: isProject ? 1 : 0,
              payload: data,
              type_id: data.type_property[0]?.id ?? 1
            }
          })
            .unwrap()
            .then(res => {
              toast.success("Borrador guardado", {
                duration: 3000,
                position: "bottom-center" as const,
                style: {
                  backgroundColor: "white",
                  padding: "0.5rem 0.5rem"
                }
              });
              resolve(void 0);
            })
            .catch(e => {
              HToastCatchServerError(e);
              reject(void 0);
            })
            .finally(() => {
              reject(void 0);
            });
        } else {
          reject(void 0);
        }
      });
    },
    onCancel: () => {
      return new Promise(resolve => {
        resolve(void 0);
      });
    },
    onProcessStart: state => {
      if (state === "saving") onSendEventBlockerStart();
      if (state == "checking") onSendEventBlockerStart();

      console.log("onProcessStart", state);
    },
    onProcessEnd: state => {
      if (state === "saving") onSendEventBlockerEnd();
      if (state === "checking") onSendEventBlockerEnd();
    }
  });

  const exceptHaveTooManyDraft = () => {
    return warningAlert({
      title: "Aviso",
      message:
        "Solo puedes tener hasta 20 borradores. Elimina uno existente para crear uno nuevo.",
      confirmBtnMessage: "Ir al listado de borradores",
      declineBtnMessage: "Cancelar"
    }).then(result => {
      if (result.type.confirm) {
        navigate(C_PATH_ROUTES.REALESTATE.DRAFT_LIST);
      }

      if (result.type.close) {
        navigate(C_PATH_ROUTES.REALESTATE.LIST);
      }
    });
  };

  /**
   * Crea un nuevo borrador de propiedad
   */
  const createNewDraft = () => {
    status.current = EStatus.CREATING;
    const { type } = getQuery();
    const typeId = Number(type) || C_REALESTATE_TYPES.APARTMENT.id;
    // if (!type || Number.isNaN(typeId)) {
    //   toast.error("Seleccione el tipo de propiedad para crear el borrador.");
    //   // Opcional: redirigir a la lista de borradores o permanecer en la vista actual
    //   return;
    // }
    const request = CreateDraft({
      body: {
        is_project: isProject ? 1 : 0,
        payload: {},
        type_id: typeId
      }
    }).unwrap();

    const TYPES = isProject ? O_REALESTATE_TYPES_PROJECT : O_REALESTATE_TYPES;

    request
      .then(res => {
        FORM_CONFIG.setValue(CPropertyFormKeys.type_property, [TYPES[typeId]], {
          shouldDirty: false,
          shouldValidate: false,
          shouldTouch: false
        });
        const path = isProject
          ? C_PATH_ROUTES.REALESTATE.DRAFT_PROJECT_UPDATE(Number(res.data.id))
          : C_PATH_ROUTES.REALESTATE.DRAFT_UPDATE(Number(res.data.id));
        // Establecer el draft_id en el formulario
        FORM_CONFIG.setValue(CPropertyFormKeys.draft_id, res.data.id, {
          shouldDirty: false,
          shouldValidate: false,
          shouldTouch: false
        });

        navigate(path, { replace: true });
        status.current = EStatus.CAN_UPDATE;
      })
      .catch((e: any) => {
        if (
          e?.data.message ==
          "Solo puedes tener hasta 20 borradores. Elimina uno existente para crear uno nuevo."
        ) {
          exceptHaveTooManyDraft();
        } else {
          HToastCatchServerError(e);
        }
      });
  };

  /**
   * Aplica los datos de un borrador existente al formulario
   */
  const applyDraft = (ID?: number) => {
    status.current = EStatus.APPLYING;
    onSendEventBlockerStart();
    GetDraft({
      params: {
        id: ID || Number(params.idDraft)
      }
    })
      .unwrap()
      .then(async res => {
        if (res.data.payload) {
          const data = await patchPayloadDraft(res);

          FORM_CONFIG.reset(
            {
              ...data,

              [CPropertyFormKeys.business_types_property]: data.is_project
                ? [C_BUSINESS_TYPE.SALE]
                : data.business_types_property
            },
            {
              keepDirty: false,
              keepTouched: false,
              keepDefaultValues: false,
              keepIsSubmitted: false,
              keepSubmitCount: false
            }
          );
          status.current = EStatus.CAN_UPDATE;
        }
        onSendEventBlockerEnd();
      })
      .catch(e => {
        HToastCatchServerError(e);
        status.current = EStatus.INITIALIZING;
        onSendEventBlockerEnd();
        infoAlert({
          title: "Borrador no valido",
          message: "Este borrador ya fue eliminado o es invalido",
          confirmBtnMessage: "Continuar con uno nuevo",
          declineBtnMessage: "Ir a listado borradores"
        })
          .then(() => {
            createNewDraft();
          })
          .catch(() => {
            navigate(C_PATH_ROUTES.REALESTATE.DRAFT_LIST);
          });
      });
  };

  /**
   * Solicita al usuario continuar con el último borrador o crear uno nuevo
   */
  const promptLastDraftOrNew = () => {
    // evitar múltiples prompts si ya cambiamos estado
    if (status.current !== EStatus.INITIALIZING) return;

    GetDraftList({ query: { per_page: 50 } })
      .unwrap()
      .then((res: IDraftListResponse) => {
        const sameTypeDrafts = (res.data || []).filter(
          (d: IDraftItem) => d.is_project === (isProject ? 1 : 0)
        );

        if (!sameTypeDrafts.length) {
          createNewDraft();
          return;
        }

        const last = maxBy(sameTypeDrafts, d => new Date(d.updated_at).getTime());
        if (!last) {
          createNewDraft();
          return;
        }

        infoAlert({
          title: "Encontramos trabajo sin terminar",
          message: "¿Deseas continuar donde lo dejaste?",
          confirmBtnMessage: "Si, abrir borrador",
          declineBtnMessage: "No"
        }).then(async result => {
          if (result.type.confirm) {
            const path = isProject
              ? C_PATH_ROUTES.REALESTATE.DRAFT_PROJECT_UPDATE(Number(last.id))
              : C_PATH_ROUTES.REALESTATE.DRAFT_UPDATE(Number(last.id));
            navigate(path, { replace: true });
            applyDraft(Number(last.id));
          } else {
            createNewDraft();
          }
        });
      })
      .catch(() => {
        createNewDraft();
      });
  };

  /**
   * Evalúa el tipo de formulario y ejecuta la acción correspondiente
   */
  const EvaluateTypeFormDraft = debounce(() => {
    const { isDraft, isFormEdit } = feedBackFormProperty(location.pathname);

    if (status.current !== EStatus.INITIALIZING) {
      return;
    }
    if (isFormEdit) {
      return;
    }
    if (isDraft) {
      applyDraft();
    } else {
      // Preguntar si desea continuar con el último borrador o crear uno nuevo
      promptLastDraftOrNew();
    }
  }, 500);

  useEffect(() => {
    EvaluateTypeFormDraft();
    return () => {
      // Cancelar operaciones pendientes al desmontar
      EvaluateTypeFormDraft.cancel();
    };
  }, []);

  return {};
};

export default usePropertyDraftV2;

const patchPayloadDraft = (e: IDraftDetailResponse) => {
  return new Promise<IPropertyForm>(resolve => {
    const TYPE = e.data.is_project ? O_REALESTATE_TYPES_PROJECT : O_REALESTATE_TYPES;

    // Implement your patch logic here

    // support for old draft
    if (
      Object.keys(e.data.payload).length > 0 &&
      Object.keys(e.data.payload).includes("encode")
    ) {
      const payload = HDecodeObj((e.data.payload as { encode: string })["encode"]);

      return resolve({
        ...CPropertyForm,
        ...payload,
        [CPropertyFormKeys.draft_id]: e.data.id
      } as IPropertyForm);
    }
    // support for current draft
    if (Object.keys(e.data.payload).length > 0) {
      const payload = e.data.payload;
      return resolve({
        ...CPropertyForm,
        ...payload,
        [CPropertyFormKeys.images_property]: onValidateImages(
          e.data.images,
          (e.data.payload as IPropertyForm)?.[CPropertyFormKeys.images_property]
        ),
        [CPropertyFormKeys.draft_id]: e.data.id
      } as IPropertyForm);
    }
    // support for empty draft
    if (e.data.type_id) {
      resolve({
        ...(CPropertyForm as any),
        [CPropertyFormKeys.type_property]: [TYPE[e.data.type_id]],
        [CPropertyFormKeys.draft_id]: e.data.id
      } as IPropertyForm);
      return;
    }

    resolve({
      ...(CPropertyForm as any),
      [CPropertyFormKeys.type_property]: [C_REALESTATE_TYPES.APARTMENT],
      [CPropertyFormKeys.draft_id]: e.data.id
    });
  });
};

type DraftImage = {
  id: number;
  path: string;
  order: number | null;
  created_at: string;
  updated_at: string;
};

const onValidateImages = (
  imagesDraft: DraftImage[] = [],
  imagesFormDraft: IFileData[] = []
): IFileData[] => {
  // Solo usar imágenes del draft. Tomar el orden desde imagesFormDraft
  // si la imagen existe allí; en caso contrario, usar el orden del draft
  // o un valor por defecto.
  const draftFiles = imagesDraft
    .map(img => {
      const data = HGenerateFileData({ file: img.path, id: img.id, comment: "" });
      if (!data) return null;

      const matchInForm = imagesFormDraft.find(f => {
        const fid = f?.id !== undefined && f?.id !== null ? String(f.id) : "";
        const did = img?.id !== undefined && img?.id !== null ? String(img.id) : "";
        return fid !== "" && did !== "" && fid === did;
      });

      const resolvedOrder =
        (matchInForm &&
          typeof matchInForm.order === "number" &&
          matchInForm.order) ??
        (typeof img.order === "number" ? img.order : undefined) ??
        data.order ??
        0;

      return {
        ...data,
        order: resolvedOrder
      } as IFileData;
    })
    .filter((d): d is IFileData => d !== null);

  return orderBy<IFileData>(draftFiles, ["order"], ["asc"]);
};
